import { DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ProductReviewsComponentDef } from '@df/catalog-ui/review/product-reviews.component.def';
import { IModelDataV1 } from '@df/catalog/model';
import { Review } from '@df/catalog/review/review';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { RatingComponent } from '../../../ui/common/rating.component';
import { InputWrapBodyDirective } from '../../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../../ui/form/input-wrap.component';
import { MuiInputDirective } from '../../../ui/form/mui-input.directive';

declare module '@df/catalog/review/review' {
    export interface IReviewData extends IModelDataV1, IRating {
        ratings?: {
            rating?: string;
            rating_id?: number;
            rating_text?: string;
        }[];
        replies?: {
            message: string;
        }[];
    }
}

@Component({
    selector: 'product-reviews',
    templateUrl: './product-reviews.html',
    styleUrl: './product-reviews.scss',
    standalone: true,
    imports: [
        DatePipe,
        DecimalPipe,
        FormsModule,
        InputWrapBodyDirective,
        InputWrapComponent,
        MuiInputDirective,
        RatingComponent,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductReviewsComponent extends ProductReviewsComponentDef implements OnInit, OnChanges {
    @Input()
    productName?: string;

    @Input()
    productData?: any;

    selectedSort = 'mostRecent';

    sortedReviews: Review[] | undefined = [];
    averageRating: number = 0;
    averageRatingNumber: number = 0;

    protected readonly timeoutService = injectTimeoutService();

    override ngOnInit() {
        super.ngOnInit();

        this.timeoutService.setTimeout(() => {
            this.calculateAverageRating();
            this.calculateAverageRatingNumber();
        }, 500);
    }

    override ngOnChanges(): void {
        super.ngOnChanges();

        if (this.reviews) {
            this.calculateAverageRating();
            this.calculateAverageRatingNumber();
        }
    }

    sortReviews(): void {
        this.sortedReviews = this.reviews?.sort((a, b) => {
            switch (this.selectedSort) {
                case 'mostRecent':
                    return new Date(b.data.date).getTime() - new Date(a.data.date).getTime();
                case 'oldest':
                    return new Date(a.data.date).getTime() - new Date(b.data.date).getTime();
                case 'highestRating':
                    return b.data.rating - a.data.rating;
                case 'lowestRating':
                    return a.data.rating - b.data.rating;
            }
        });

        this.detectChanges();
    }

    calculateAverageRating(): void {
        if (this.productData?.data?.reviews?.rating) {
            this.averageRating = this.productData.data.reviews.rating;
        } else if (this.reviews && this.reviews.length > 0) {
            const validReviews = this.reviews.filter(review => review.data?.rating && review.data.rating > 0);

            if (validReviews.length > 0) {
                const totalRating = validReviews.reduce((sum, review) => sum + (review.data?.rating || 0), 0);
                this.averageRating = totalRating / validReviews.length;
            } else {
                this.averageRating = 0;
            }
        } else {
            this.averageRating = 0;
        }

        this.detectChanges();
    }

    calculateAverageRatingNumber(): void {
        if (this.reviews && this.reviews.length > 0) {
            const ratings = this.reviews
                .map(review => review.data?.ratings?.find(rating => rating.rating_id === 6335))
                .filter(rating => rating !== undefined) as any[];

            if (ratings.length > 0) {
                const sumOfRatings = ratings.reduce((sum, rating) => sum + rating.rating, 0);
                const averageRatingNumber = sumOfRatings / ratings.length;

                this.averageRatingNumber = Math.round(averageRatingNumber);
            }
        }

        this.detectChanges();
    }

    getRatingValueById(ratings: any[], id: number): any {
        const rating = ratings?.find(r => r.rating_id === id);

        return rating ? rating.rating : null;
    }

    getReplyTextById(ratings: any[], id: number): string {
        const rating = ratings?.find(r => r.rating_id === id);

        return rating ? rating.rating_text : '';
    }
}
